import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { memo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { ensureCurrentUser, ensureUser } from '../../../util/data';
import useIsInView from '../../../util/hooks/useIsInView';
import { FormattedMessage } from '../../../util/reactIntl';
import { AvatarLarge } from '../../Avatar/Avatar';
import NamedLink from '../../Links/NamedLink/NamedLink';
import ExpandableBio from './ExpandableBio';

// .avatar
const avatarSx = {
  minWidth: '30vw',
  minHeight: '30vw',
  '@media (min-width:900px)': { minWidth: '15vw', minHeight: '15vw' },
};

// .link
const linkSx = {
  whiteSpace: 'nowrap',
  width: { xs: '100%', sm: '100%', md: 'auto' },
};

// .links (without bio missing above)
const linksSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  marginTop: '13px',
  [theme.breakpoints.up('md')]: {
    ...theme.mixins.marketplaceDefaultFontStyles[theme.breakpoints.up('md')],
    margin: '16px 0 0 0',
  },
});

// .links + .withBioMissingAbove (bio missing above → md margin-top 16px)
const linksWithBioMissingAboveSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  marginTop: '13px',
  [theme.breakpoints.up('md')]: {
    ...theme.mixins.marketplaceDefaultFontStyles[theme.breakpoints.up('md')],
    margin: '16px 0 0 0',
    marginTop: '16px',
  },
});

// .linkSeparator
const linkSeparatorSx = (theme) => ({ margin: '0 10px', color: theme.tokens.marketplaceColor });

// .editProfileMobile
const editProfileMobileSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  display: 'inline',
  [theme.breakpoints.up('md')]: {
    ...theme.mixins.marketplaceDefaultFontStyles[theme.breakpoints.up('md')],
    display: 'none',
  },
});

// .editProfileDesktop
const editProfileDesktopSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  display: 'none',
  marginBottom: 0,
  whiteSpace: 'nowrap',
  [theme.breakpoints.up('md')]: {
    ...theme.mixins.marketplaceDefaultFontStyles[theme.breakpoints.up('md')],
    display: 'inline',
  },
});

const UserCardComponent = ({ user = null, currentUser = null, getTranslation = null }) => {
  const intl = useIntl();

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id && ensuredCurrentUser.id && ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;
  const { displayName, bio } = ensuredUser.attributes.profile;
  const { translations, defaultLocale } = getTranslation(user.id.uuid, 'user', intl.locale);
  const isTranslated = !!defaultLocale && defaultLocale !== intl.locale;
  const translatedBio = translations.bio || bio;

  const hasBio = !!bio;
  const linksSxResolved = hasBio ? linksSx : linksWithBioMissingAboveSx;

  const separator = !isCurrentUser ? (
    <Box component="span" sx={linkSeparatorSx}>
      •
    </Box>
  ) : null;

  const targetRef = useRef(null);
  const isInViewPort = useIsInView(targetRef, { threshold: 0.7 }, true);

  const editProfileMobile = (
    <Box component="span" sx={editProfileMobileSx}>
      <Box component="span" sx={linkSeparatorSx}>
        •
      </Box>
      <NamedLink name="ProfileSettingsPage">
        <FormattedMessage id="ListingPage.editProfileLink" />
      </NamedLink>
    </Box>
  );

  const editProfileDesktop = isCurrentUser ? (
    <Box component={NamedLink} name="ProfileSettingsPage" sx={editProfileDesktopSx}>
      <FormattedMessage id="ListingPage.editProfileLink" />
    </Box>
  ) : null;

  const links = ensuredUser.id ? (
    <Box component="p" sx={linksSxResolved}>
      <NamedLink name="ProfilePage" params={{ id: ensuredUser.id.uuid }}>
        <Button sx={linkSx} variant="outlined">
          <FormattedMessage id="UserCard.viewProfileLink" />
        </Button>
      </NamedLink>
      {isCurrentUser ? (
        <>
          {separator}
          {editProfileMobile}
        </>
      ) : null}
    </Box>
  ) : null;

  const hasSpokenLanguages =
    user.attributes.profile.publicData?.spokenLanguages?.length > 0;
  const spokenLanguages = user.attributes.profile.publicData?.spokenLanguages;

  return (
    <Box sx={{ display: { xs: 'block', md: 'flex' } }} width={1}>
      <Box
        display="flex"
        ref={targetRef}
        sx={{
          width: { xs: 1, md: '0.3' },
          justifyContent: { xs: 'center', md: 'flex-start' },
        }}
      >
        {isInViewPort ? <AvatarLarge sx={avatarSx} user={user} /> : null}
      </Box>

      <Box justifyContent="center" sx={{ width: { xs: 1, md: '0.7' } }}>
        <Typography
          sx={{
            textAlign: { xs: 'center', md: 'left' },
            mt: { xs: 2, md: 0 },
            mb: { xs: 1, md: 0 },
          }}
          variant="h4"
        >
          <FormattedMessage id="UserCard.heading" values={{ name: displayName }} />
        </Typography>

        {hasSpokenLanguages ? (
          <Box sx={{ my: 2 }}>
            <Typography component="h2" gutterBottom variant="h6">
              <FormattedMessage id="ProfilePage.spokenLanguages" />
            </Typography>
            <Stack direction="row" flexWrap="wrap" spacing={1}>
              {spokenLanguages.map((lang) => (
                <Chip
                  key={lang}
                  label={<FormattedMessage id={`SpokenLanguage.${lang}`} />}
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
        ) : null}

        {hasBio ? (
          <ExpandableBio bio={translatedBio} isTranslated={isTranslated} variant="desktop" />
        ) : null}

        {hasBio ? <ExpandableBio bio={bio} variant="mobile" /> : null}
        <Box sx={{ width: 1 }}>{links}</Box>
        {editProfileDesktop}
      </Box>
    </Box>
  );
};

UserCardComponent.displayName = 'UserCard';
const UserCard = memo(UserCardComponent);

export default UserCard;
